import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import { ProgressBar } from "@builder/components/onboard/components/progressbar/ProgressBar";
import bwipjs from "bwip-js/browser";
import { useMemo, useState } from "react";
import "./OnboardComplete.scss";
import { useAppDispatch } from "@builder/store/store";
import { closePopup } from "@builder/store/slices/popupSlice";
import useWindowSize from "@builder/hooks/useWindowSize";

const appDownloadOptions: Record<
    string,
    {
        userAgentRegex: RegExp;
        url: string;
        text: string;
        icon: string;
    }
> = {
    ios: {
        userAgentRegex: /iphone|ipad|ipod|mac/i,
        url: "https://apps.apple.com/gb/app/final-pos/id6741775261",
        text: "Download for iOS",
        icon: "/download-mac.svg"
    },
    android: {
        userAgentRegex: /android/i,
        url: "https://play.google.com/store/apps/details?id=com.finalpos.render_native",
        text: "Download for Android",
        icon: "/download-android.svg"
    },
    windows: {
        userAgentRegex: /windows nt|win64|win32/i,
        url: "https://apps.microsoft.com/detail/9nlqwgfz27p2",
        text: "Download for Windows",
        icon: "/download-windows.svg"
    }
} as const;

type OSOption = keyof typeof appDownloadOptions;

// Pre-generate QR code SVGs for each platform – this runs once at module load
const qrCodeSvgs: Record<OSOption, string> = (Object.keys(appDownloadOptions) as OSOption[]).reduce((acc, os) => {
    acc[os] = bwipjs.toSVG({
        bcid: "qrcode", // barcode type
        text: appDownloadOptions[os].url, // URL encoded in the QR
        includetext: false // no human-readable text under the code
    });
    return acc;
}, {} as Record<OSOption, string>);

const DownloadOSButton: React.FC<{ os: OSOption }> = ({ os }) => {
    const svg = useMemo(() => <img src={appDownloadOptions[os].icon} alt={os} />, [os]);

    return (
        <button className="download-os-button" onClick={() => window.open(appDownloadOptions[os].url, "_blank")}>
            {svg}
            {appDownloadOptions[os].text}
        </button>
    );
};

const CheckmarkUseCase: React.FC<{ text: string }> = ({ text }) => {
    return (
        <div className="checkmark-use-case">
            <img src="/weird-dashed-checkmark.svg" alt="checkmark" />
            <OliverText text={text} size="m" weight="light" />
        </div>
    );
};

const StepOne = ({ isMobile = false }: { isMobile?: boolean }) => {
    const currentOS = useMemo<OSOption>(() => {
        const userAgentString = window.navigator.userAgent.toLowerCase();
        const found = Object.keys(appDownloadOptions).find(key => appDownloadOptions[key].userAgentRegex.test(userAgentString));
        return found ?? "windows";
    }, []);
    const [deviceTab, setDeviceTab] = useState<OSOption>(currentOS);

    if (isMobile) {
        return (
            <div className="step-one">
                <div className="download-default">
                    <OliverText text="Your POS is all set and waiting for you!" size="l" weight="medium-heavy" />
                    <p className="get-app">
                        Get the <span>Final POS app</span> to launch it.{" "}
                    </p>
                </div>
                <div className="download-default">
                    <DownloadOSButton os={currentOS} />
                    <div className="divider" />
                </div>
                <div className="other-devices">
                    <OliverText text="Available on other devices:" size="s" color="base-blue" weight="light" />
                    {Object.keys(appDownloadOptions)
                        .filter(tab => tab !== currentOS)
                        .map(tab => (
                            <DownloadOSButton key={tab} os={tab} />
                        ))}
                </div>
            </div>
        );
    }

    return (
        <div className="step-one">
            <div className="download-default">
                <OliverText text="To launch your POS, you’ll need to download the Final POS app." size="s" />
                <DownloadOSButton os={currentOS} />

                <div className="divider" />
            </div>

            <div className="install-other-devices">
                <OliverText text="Or install on other devices" size="s" color="base-blue" />

                <div className="first-time-publish-device-tabs">
                    <div className="device-tab">
                        {Object.keys(appDownloadOptions).map(tab => (
                            <div key={tab} className={`tab ${deviceTab === tab ? "active" : ""}`} onClick={() => setDeviceTab(tab)}>
                                <OliverText text={tab} size="m" weight="light" color={deviceTab === tab ? "base-blue" : "base-grey"} />
                            </div>
                        ))}
                    </div>
                    <div className="device-download-content">
                        {(() => {
                            const dataUri = `data:image/svg+xml;base64,${btoa(qrCodeSvgs[deviceTab])}`;
                            return <img width={120} height={120} className="qr-code-svg" src={dataUri} alt="QR code" />;
                        })()}

                        <DownloadOSButton os={deviceTab} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const LastStep = () => {
    return (
        <div className="step-two">
            <div className="download-default">
                <OliverText text="Your POS is published, but it’s just a shell. Want to set up your store before launching it?" size="m" />
                <div className="manage-use-cases">
                    <OliverText text="Use manage to:" size="m" />
                    <div className="list">
                        <CheckmarkUseCase text="Set up your store" />
                        <CheckmarkUseCase text="Add products" />
                        <CheckmarkUseCase text="Add employees" />
                        <CheckmarkUseCase text="Add customers" />
                        <CheckmarkUseCase text="Add transactions" />
                        <CheckmarkUseCase text="Add reports" />
                        <CheckmarkUseCase text="Add settings" />
                    </div>
                </div>
                <button className="go-to-manage-button" onClick={() => window.open(`${import.meta.env.VITE_HUB_URL}/select-company`, "_blank")}>
                    Go to manage
                </button>
            </div>
        </div>
    );
};

const steps = [StepOne, LastStep];

const OnboardComplete = () => {
    const [stepIdx, setStepIdx] = useState<number>(0);
    const dispatch = useAppDispatch();
    const { isMobile } = useWindowSize();

    const closeThePopup = () => {
        dispatch(closePopup());
    };

    if (isMobile) {
        return (
            <div className="first-time-publish-mobile">
                <div className="logo">
                    <img src="/final-logo-full.svg" alt="final-logo-full" />
                </div>
                <div className="step">
                    <StepOne isMobile={isMobile} />
                </div>
            </div>
        );
    }

    return (
        <div className="first-time-publish">
            <div className="step-with-nav">
                <div className="step">
                    <nav>
                        <OliverText
                            text="Congrats! your POS is officially live! Now, let's get you ready to actually run it"
                            size="m"
                            weight="medium-heavy"
                            color="base-grey"
                        />
                        <ProgressBar currentStepIdx={stepIdx} totalSteps={steps.length} />
                    </nav>

                    {(() => {
                        const StepComponent = steps[stepIdx];
                        return <StepComponent isMobile={isMobile} />;
                    })()}
                </div>
                <div className="bottom-nav">
                    <Button
                        buttonStyle="grey"
                        size="small"
                        className={`next-btn ${stepIdx > 0 ? "back-btn" : "hide"}`}
                        onClick={() => setStepIdx(stepIdx - 1)}
                        text="Back"
                    />
                    <Button
                        buttonStyle={stepIdx === steps.length - 1 ? "secondary" : "primary"}
                        size="small"
                        className={`next-btn`}
                        onClick={stepIdx === steps.length - 1 ? () => closeThePopup() : () => setStepIdx(stepIdx + 1)}
                        text={stepIdx === steps.length - 1 ? "I will explore Builder first" : "Next"}
                    />
                </div>
            </div>
            {/* future design, commented out for now */}
            {/* {stepIdx === steps.length - 1 && <div className="right-side"></div>} */}
        </div>
    );
};

export default OnboardComplete;
